import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { WebhookEventType } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Select } from '../components/ui/input.js';
import { FormBanner } from '../components/ui/form.js';
import { EmptyState } from '../components/ui/empty-state.js';

interface Webhook {
  id: string;
  url: string;
  events: WebhookEventType[];
  active: boolean;
  description?: string;
  created_at: string;
}

type AttemptStatus = 'pending' | 'in_flight' | 'completed' | 'expired';
type HistoryStatus = 'success' | 'http_error' | 'transport_error' | 'timeout';

interface AttemptHistoryEntry {
  status: HistoryStatus;
  http_status?: number;
  error_message?: string;
  duration_ms: number;
  attempted_at: string;
}

interface Attempt {
  id: string;
  envelope_id: string;
  event_type: WebhookEventType;
  status: AttemptStatus;
  attempt_number: number;
  next_attempt_at: string;
  last_attempted_at: string | null;
  last_http_status: number | null;
  last_error: string | null;
  attempt_history: AttemptHistoryEntry[];
  completed_at: string | null;
  created_at: string;
}

interface AttemptsResponse {
  data: Attempt[];
}

const ALL_EVENT_TYPES: WebhookEventType[] = [
  'event.scheduled',
  'event.reminder.sent',
  'event.confirmed',
  'campaign.completed',
  'contact.dnc.added',
];

const ATTEMPT_TONE: Record<AttemptStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  pending: 'neutral',
  in_flight: 'info',
  completed: 'success',
  expired: 'danger',
};

const HISTORY_TONE: Record<HistoryStatus, 'success' | 'danger' | 'warning'> = {
  success: 'success',
  http_error: 'danger',
  transport_error: 'danger',
  timeout: 'warning',
};

export function WebhookDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [testEventType, setTestEventType] = useState<WebhookEventType>('event.scheduled');
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const webhookQ = useQuery({
    queryKey: ['webhook', id],
    queryFn: () => api.get(`/v1/webhooks/${id}`) as Promise<Webhook>,
    enabled: !!id,
  });

  const attemptsQ = useQuery({
    queryKey: ['webhook-attempts', id],
    queryFn: () => api.get(`/v1/webhooks/${id}/attempts?limit=50`) as Promise<AttemptsResponse>,
    enabled: !!id,
    // Poll while there are deliveries in motion; the durable worker ticks
    // every 5s so 15s catches state changes within ~one cycle.
    refetchInterval: 15_000,
  });

  const deleteOne = useMutation({
    mutationFn: () => api.delete(`/v1/webhooks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      navigate('/webhooks');
    },
  });

  const fireTest = useMutation({
    mutationFn: () =>
      api.post(`/v1/webhooks/${id}/test`, { event_type: testEventType }) as Promise<{
        envelope_id: string;
        scheduled: boolean;
        message: string;
      }>,
    onSuccess: (resp) => {
      setTestError(null);
      setTestResult(`Queued envelope ${resp.envelope_id}. ${resp.message}`);
    },
    onError: (err) => {
      setTestResult(null);
      setTestError(extractDetail(err));
    },
  });

  if (webhookQ.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (webhookQ.error) return <FormBanner message={extractDetail(webhookQ.error)} />;
  if (!webhookQ.data) return <FormBanner message="Webhook not found" />;
  const w = webhookQ.data;

  return (
    <div className="max-w-3xl space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <Link to="/webhooks" className="text-sm text-ink-muted hover:text-ink">← Webhooks</Link>
          <h1 className="text-2xl font-semibold mt-1 break-all font-mono text-base">{w.url}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge tone={w.active ? 'success' : 'neutral'}>
              {w.active ? 'active' : 'paused'}
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            if (window.confirm(`Delete webhook for ${w.url}?`)) deleteOne.mutate();
          }}
          disabled={deleteOne.isPending}
        >
          Delete
        </Button>
      </header>

      <Card>
        <CardHeader><CardTitle>Subscription</CardTitle></CardHeader>
        <CardBody>
          <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
            <dt className="text-ink-muted">URL</dt>
            <dd className="font-mono text-xs break-all">{w.url}</dd>
            <dt className="text-ink-muted">Events</dt>
            <dd>
              <div className="flex flex-wrap gap-1">
                {w.events.map((e) => (
                  <Badge key={e} tone="neutral">{e}</Badge>
                ))}
              </div>
            </dd>
            <dt className="text-ink-muted">Description</dt>
            <dd>{w.description ?? <span className="text-ink-muted">—</span>}</dd>
            <dt className="text-ink-muted">Created</dt>
            <dd>{new Date(w.created_at).toLocaleString()}</dd>
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Send a test delivery</CardTitle></CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-ink-muted">
            Fires a synthetic event envelope at this webhook so you can verify your receiver
            without waiting for a real domain event. The payload includes
            {' '}<code>is_test: true</code>.
          </p>
          <div className="flex items-stretch gap-2">
            <Select
              value={testEventType}
              onChange={(e) => setTestEventType(e.target.value as WebhookEventType)}
              className="flex-1"
            >
              {ALL_EVENT_TYPES.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </Select>
            <Button
              type="button"
              onClick={() => {
                setTestResult(null);
                setTestError(null);
                fireTest.mutate();
              }}
              disabled={fireTest.isPending}
            >
              {fireTest.isPending ? 'Sending…' : 'Send test'}
            </Button>
          </div>
          {testResult && (
            <p className="text-sm text-success">{testResult}</p>
          )}
          {testError && (
            <p className="text-sm text-danger">{testError}</p>
          )}
        </CardBody>
      </Card>

      <DeliveryLogCard
        attempts={attemptsQ.data?.data ?? []}
        isLoading={attemptsQ.isLoading}
        error={attemptsQ.error}
        onRefresh={() => attemptsQ.refetch()}
      />
    </div>
  );
}

// ============================================================================
// Delivery log
// ============================================================================

interface DeliveryLogCardProps {
  attempts: Attempt[];
  isLoading: boolean;
  error: unknown;
  onRefresh: () => void;
}

function DeliveryLogCard({ attempts, isLoading, error, onRefresh }: DeliveryLogCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Delivery log</CardTitle>
        <Button variant="ghost" size="sm" onClick={onRefresh}>
          Refresh
        </Button>
      </CardHeader>
      <CardBody className="p-0">
        {isLoading && <p className="px-4 py-3 text-sm text-ink-muted">Loading…</p>}
        {!!error && (
          <p className="px-4 py-3 text-sm text-danger">
            Failed to load: {(error as Error).message}
          </p>
        )}
        {!isLoading && !error && attempts.length === 0 && (
          <EmptyState
            title="No deliveries yet"
            description="Once a subscribed event fires, attempts will appear here. (In dev with no DATABASE_URL, the in-process scheduler keeps state in memory only — this list will always be empty.)"
          />
        )}
        {attempts.length > 0 && (
          <ul className="divide-y divide-border">
            {attempts.map((a) => (
              <AttemptRow key={a.id} attempt={a} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function AttemptRow({ attempt }: { attempt: Attempt }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const tone = ATTEMPT_TONE[attempt.status];
  const lastTime = attempt.completed_at ?? attempt.last_attempted_at ?? attempt.created_at;
  const summary =
    attempt.status === 'completed' ? `HTTP ${attempt.last_http_status ?? '?'}`
    : attempt.status === 'expired' ? (attempt.last_error ?? 'exhausted retries')
    : attempt.status === 'in_flight' ? 'in flight'
    : `next attempt ${new Date(attempt.next_attempt_at).toLocaleTimeString()}`;
  const hasHistory = attempt.attempt_history.length > 0;

  return (
    <li className="px-4 py-3 text-sm">
      <div className="flex items-center gap-3">
        <Badge tone={tone}>{attempt.status.replace('_', ' ')}</Badge>
        <span className="text-ink-muted">{attempt.event_type}</span>
        <span className="text-ink-muted">·</span>
        <span className="text-ink-muted">attempt {attempt.attempt_number}/8</span>
        <span className="flex-1" />
        <span className="text-xs text-ink-muted">
          {new Date(lastTime).toLocaleString()}
        </span>
        {hasHistory && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-ink-muted hover:text-ink"
          >
            {expanded ? 'Hide' : 'History'}
          </button>
        )}
      </div>
      <div className="mt-1 ml-1 text-xs text-ink-muted truncate" title={summary}>
        {summary}
      </div>
      {expanded && hasHistory && (
        <ol className="mt-2 ml-1 space-y-1 border-l border-border pl-3">
          {attempt.attempt_history.map((h, i) => (
            <li key={i} className="flex items-center gap-2 text-xs">
              <Badge tone={HISTORY_TONE[h.status]}>{h.status.replace('_', ' ')}</Badge>
              {h.http_status != null && <span className="text-ink-muted">HTTP {h.http_status}</span>}
              {h.error_message && (
                <span className="text-danger truncate" title={h.error_message}>
                  {h.error_message}
                </span>
              )}
              <span className="text-ink-muted">·</span>
              <span className="text-ink-muted">{h.duration_ms}ms</span>
              <span className="text-ink-muted">·</span>
              <span className="text-ink-muted">{new Date(h.attempted_at).toLocaleString()}</span>
            </li>
          ))}
        </ol>
      )}
      <div className="mt-1 ml-1 text-[10px] text-ink-muted/70 font-mono" title={attempt.envelope_id}>
        envelope {attempt.envelope_id}
      </div>
    </li>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
